import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiJson } from '../apiClient';
import { dateKeyFromString, getLocalDateKey } from '../utils/reportDates';
import './SmsInboundMonitorPage.css';

function formatMoney(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString();
}

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'posted') return 'posted';
  if (s === 'pending_review') return 'pending';
  if (s === 'failed') return 'failed';
  return 'received';
}

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'posted', label: 'Posted' },
  { key: 'pending_review', label: 'Pending' },
  { key: 'failed', label: 'Failed' },
];

export default function SmsInboundMonitorPage({ onNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [reprocessId, setReprocessId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');

  const refresh = useCallback(async (isManual = false) => {
    try {
      if (isManual) setRefreshing(true);
      else setLoading(true);
      const data = await apiJson('/owner/sms-inbounds');
      setItems(Array.isArray(data.items) ? data.items : []);
      setMessage('');
    } catch (err) {
      setMessage(err?.message || 'Could not load SMS monitor data.');
      if (!isManual) setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void refresh(false);
  }, [refresh]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const statusOk = statusFilter === 'all' || String(it.status) === statusFilter;
      const hay = `${it.sender || ''} ${it.message || ''} ${it.parsedService || ''} ${it.parsedChannel || ''}`.toLowerCase();
      const searchOk = !search.trim() || hay.includes(search.trim().toLowerCase());
      return statusOk && searchOk;
    });
  }, [items, statusFilter, search]);

  const stats = useMemo(() => {
    const todayKey = getLocalDateKey();
    return {
      total: items.length,
      posted: items.filter((x) => x.status === 'posted').length,
      pending: items.filter((x) => x.status === 'pending_review').length,
      failed: items.filter((x) => x.status === 'failed').length,
      today: items.filter((x) => dateKeyFromString(x.receivedAt || x.createdAt) === todayKey).length,
    };
  }, [items]);

  const handleReprocess = async (id) => {
    setReprocessId(id);
    try {
      await apiJson(`/owner/sms-inbounds/${id}/reprocess`, { method: 'POST' });
      await refresh(true);
      setMessage('Reprocess complete.');
    } catch (err) {
      setMessage(err?.message || 'Could not reprocess this SMS.');
    } finally {
      setReprocessId(null);
    }
  };

  return (
    <div className="sms-monitor-root page-bleed">
      <header className="sms-monitor-header">
        <h1>SMS Monitor (Owner)</h1>
        <p>Track auto-posted, pending, and failed SMS from gateway in real time.</p>
      </header>

      <div className="sms-monitor-stats">
        <article className="sms-stat-card">
          <span>Total</span>
          <strong>{stats.total}</strong>
        </article>
        <article className="sms-stat-card sms-stat-card--ok">
          <span>Posted</span>
          <strong>{stats.posted}</strong>
        </article>
        <article className="sms-stat-card sms-stat-card--warn">
          <span>Pending</span>
          <strong>{stats.pending}</strong>
        </article>
        <article className="sms-stat-card sms-stat-card--bad">
          <span>Failed</span>
          <strong>{stats.failed}</strong>
        </article>
        <article className="sms-stat-card">
          <span>Today</span>
          <strong>{stats.today}</strong>
        </article>
      </div>

      <div className="sms-monitor-toolbar">
        <div className="sms-monitor-filters">
          <label>
            Status
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Search
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="sender, message, service..."
            />
          </label>
        </div>
        <button type="button" className="sms-monitor-refresh-btn" onClick={() => void refresh(true)} disabled={refreshing}>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {message ? <p className="sms-monitor-message">{message}</p> : null}

      <div className="sms-monitor-table-wrap">
        {loading ? (
          <p className="sms-monitor-loading">Loading SMS monitor...</p>
        ) : filtered.length === 0 ? (
          <p className="sms-monitor-empty">No SMS records match your filter.</p>
        ) : (
          <table className="sms-monitor-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Sender</th>
                <th>Message</th>
                <th>Parsed</th>
                <th>Amount</th>
                <th>Comm.</th>
                <th>Conf.</th>
                <th>Status</th>
                <th>Tx</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id}>
                  <td>{it.receivedAt || it.createdAt || '—'}</td>
                  <td>{it.sender || '—'}</td>
                  <td className="sms-monitor-msg" title={it.message}>
                    {it.message}
                  </td>
                  <td>
                    <div>{it.parsedDirection || '—'}</div>
                    <small>
                      {(it.parsedService || '—')} / {(it.parsedChannel || '—')}
                    </small>
                  </td>
                  <td>{formatMoney(it.parsedAmount)}</td>
                  <td>{formatMoney(it.parsedCommission)}</td>
                  <td>{it.confidence == null ? '—' : `${Math.round(Number(it.confidence) * 100)}%`}</td>
                  <td>
                    <span className={`sms-status sms-status--${statusClass(it.status)}`}>{String(it.status || 'received').replace('_', ' ')}</span>
                  </td>
                  <td>{it.cashTransactionId || '—'}</td>
                  <td>
                    {it.status === 'posted' ? (
                      <span className="sms-action-muted">Done</span>
                    ) : (
                      <button
                        type="button"
                        className="sms-reprocess-btn"
                        onClick={() => void handleReprocess(it.id)}
                        disabled={reprocessId === it.id}
                      >
                        {reprocessId === it.id ? '...' : 'Reprocess'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {typeof onNavigate === 'function' ? (
        <div className="sms-monitor-footer">
          <button type="button" onClick={() => onNavigate('cash')} className="sms-monitor-back-btn">
            Back to Wakala
          </button>
        </div>
      ) : null}
    </div>
  );
}
